import { Device } from '@capacitor/device';
import {
  IonMenu,
  IonContent,
  IonMenuToggle,
  useIonAlert,
  IonActionSheet,
  IonIcon,
} from '@ionic/react';
import { documentTextOutline, prism, closeOutline } from 'ionicons/icons';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';

import packageJson from '../../package.json';
import LogOut from '../assets/LogoutWhite.svg';
import calender from '../assets/calender.svg';
import Homeicon from '../assets/homeicon.svg';
import bulb from '../assets/icons/bulb.svg';
import LogoWhite from '../assets/icons/logo-white.png';
import map from '../assets/icons/map.svg';
import Lang from '../assets/icons/translation.png';
import model937 from '../assets/model937.svg';
import toolkit from '../assets/toolkit.svg';
import { AppContext } from '../providers/AppContext';


const Menu: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const [present] = useIonAlert();
  const { dispatch, state } = useContext(AppContext);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [version, setVersion] = useState('');
  const [isDesktop, setIsDesktop] = useState(false);
  const canViewDocuments =
    state.permissions?.documents !== undefined
      ? Boolean(state.permissions.documents)
      : state.permissions?.document !== undefined
        ? Boolean(state.permissions.document)
        : true;

  const getDeviceInfo = async () => {
    const info = await Device.getInfo();
    if (info.platform === 'web') {
      setVersion(packageJson.version);
    } else if (info.platform === 'android') {
      // const currentV = await AppUpdate.getAppUpdateInfo();
      // console.log('Current Version', currentV.currentVersion);
      // const availableV = await AppUpdate.getAppUpdateInfo();
      // console.log('available Version:', availableV.availableVersion);
      // await AppUpdate.openAppStore();
      // const result = await AppUpdate.getAppUpdateInfo();
    }
  };

  useEffect(() => {
    getDeviceInfo();
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const renderMenuItem = (path: string, icon: any, label: string, isIconComponent = false) => {
    return (
      <IonMenuToggle auto-hide="false">
        <div
          onClick={() => history.push(path)}
          style={{
            display: 'flex',
            flexDirection: isDesktop ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: isDesktop ? 'center' : 'flex-start',
            gap: isDesktop ? '0px' : '16px',
            padding: isDesktop ? '8px 0px' : '14px 20px',
            margin: isDesktop ? '4px 2px' : '4px 12px',
            borderRadius: '12px',
            background: isActive(path) ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
            border: isActive(path) ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          {isIconComponent ? (
            <IonIcon icon={icon} style={{ fontSize: isDesktop ? '20px' : '22px', color: '#fff', opacity: isActive(path) ? 1 : 0.7 }} />
          ) : (
            <img src={icon} alt={label} style={{ width: isDesktop ? '20px' : '22px', height: isDesktop ? '20px' : '22px', opacity: isActive(path) ? 1 : 0.7 }} />
          )}
          <span style={{
            fontSize: isDesktop ? '9px' : '14px',
            fontWeight: isActive(path) ? 700 : 500,
            color: '#fff',
            opacity: isActive(path) ? 1 : 0.85,
            letterSpacing: '0.01em',
            textAlign: isDesktop ? 'center' : 'left',
            display: 'block',
          }}>
            {label}
          </span>
        </div>
      </IonMenuToggle>
    );
  };

  return (
    <>
      <IonMenu side="start" contentId="main" menuId="first">
        <IonContent style={{ '--background': 'linear-gradient(135deg, #1d508d 0%, #0f3460 100%)' }}>
          {/* ── Top Header Section ── */}
          <div style={{ padding: isDesktop ? '20px 2px 16px' : '30px 20px 20px', position: 'relative' }}>
            {!isDesktop && (
              <IonMenuToggle slot="end" style={{ position: 'absolute', top: 20, right: 16 }}>
                <IonIcon icon={closeOutline} style={{ fontSize: 28, color: 'rgba(255,255,255,0.6)' }} />
              </IonMenuToggle>
            )}

            <div style={{
              display: 'flex',
              flexDirection: isDesktop ? 'column' : 'row',
              alignItems: 'center',
              gap: isDesktop ? 8 : 12,
              marginTop: isDesktop ? 24 : 0
            }}>
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                padding: isDesktop ? 6 : 10,
                borderRadius: 16,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <img
                  src={process.env.REACT_APP_WHITE_LOGO || LogoWhite}
                  alt="logo"
                  style={{ width: isDesktop ? 24 : 40, height: isDesktop ? 24 : 40, objectFit: 'contain' }}
                />
              </div>
              {!isDesktop && (
                <div>
                  <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: '0.02em' }}>
                    {process.env.REACT_APP_APP_NAME || 'Hyprism'}
                  </h1>
                  <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    Enterprise Suite
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ── Menu List ── */}
          <div style={{ padding: '0 4px', marginTop: 10 }}>
            {!!state.permissions.home && renderMenuItem('/home', Homeicon, t('Menu.Home'))}

            {!!state.permissions.project && renderMenuItem('/project', prism, t('Menu.Project'), true)}

            {!!canViewDocuments && renderMenuItem('/documents', documentTextOutline, t('Menu.Documents'), true)}

            {!!state.permissions.assets && renderMenuItem('/assets', bulb, t('Menu.AC'))}

            {!!state.permissions.service && renderMenuItem('/serviceHome', toolkit, t('Menu.Service'))}

            {!!state.permissions.maintenance && renderMenuItem('/maintenanceHome', calender, t('Menu.Maintenance'))}

            {!!state.permissions.model && renderMenuItem('/model-home', model937, t('Menu.Model937'))}

            {!!state.permissions.survey && renderMenuItem('/survey-home', map, t('Survey.name'))}

            <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', margin: '15px 25px' }} />

            {/* ── Utilities ── */}
            <IonMenuToggle auto-hide="false">
              <div
                onClick={() => setShowActionSheet(true)}
                style={{
                  display: 'flex',
                  flexDirection: isDesktop ? 'column' : 'row',
                  alignItems: 'center',
                  gap: isDesktop ? '0px' : '16px',
                  padding: isDesktop ? '8px 0px' : '14px 20px',
                  margin: isDesktop ? '4px 2px' : '4px 12px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                }}
              >
                <img src={Lang} alt="lang" style={{ width: isDesktop ? '20px' : '22px', height: isDesktop ? '20px' : '22px', opacity: 0.7 }} />
                <span style={{
                  fontSize: isDesktop ? '9px' : '14px',
                  fontWeight: 500,
                  color: '#fff',
                  opacity: 0.85,
                  textAlign: isDesktop ? 'center' : 'left'
                }}>
                  {t('Utils.change_lang')}
                </span>
              </div>
            </IonMenuToggle>

            <IonMenuToggle
              auto-hide="false"
              onClick={() => {
                const message = t('Utils.are_you_sure');
                present(message, [
                  { text: t('Utils.no'), role: 'cancel', cssClass: 'secondary' },
                  {
                    text: t('Utils.yes'),
                    cssClass: 'danger',
                    handler: () => { dispatch({ type: 'logOut' }); },
                  },
                ]);
              }}
            >
              <div style={{
                display: 'flex',
                flexDirection: isDesktop ? 'column' : 'row',
                alignItems: 'center',
                gap: isDesktop ? '0px' : '16px',
                padding: isDesktop ? '8px 0px' : '14px 20px',
                margin: isDesktop ? '4px 2px' : '4px 12px',
                borderRadius: '12px',
                cursor: 'pointer',
              }}>
                <img src={LogOut} alt="logout" style={{ width: isDesktop ? '20px' : '22px', height: isDesktop ? '20px' : '22px', opacity: 0.7 }} />
                <span style={{
                  fontSize: isDesktop ? '9px' : '14px',
                  fontWeight: 500,
                  color: '#fff',
                  opacity: 0.85,
                  textAlign: isDesktop ? 'center' : 'left'
                }}>
                  {t('Header.Avatar.LogOut')}
                </span>
              </div>
            </IonMenuToggle>
          </div>

          {/* ── Footer ── */}
          <div style={{ marginTop: 'auto', padding: '40px 20px 30px', textAlign: 'center' }}>
            <p style={{ margin: 0, opacity: 0.4, color: '#fff', fontSize: '11px', fontWeight: 600 }}>
              {process.env.REACT_APP_APP_NAME || 'Hyprism'} &copy; {new Date().getFullYear()}
            </p>
            <p style={{ margin: '4px 0 0', opacity: 0.3, color: '#fff', fontSize: '10px' }}>
              {packageJson.version ? `v${packageJson.version}` : ''} • {t('Footer')}
            </p>
          </div>
        </IonContent>
      </IonMenu>
      <IonActionSheet
        isOpen={showActionSheet}
        onDidDismiss={() => setShowActionSheet(false)}
        buttons={[
          {
            text: t('Utils.en'),
            handler: () => {
              i18n.changeLanguage('en');
              localStorage.setItem('lang', 'en');
            },
          },
          {
            text: t('Utils.pt'),
            handler: () => {
              i18n.changeLanguage('pt');
              localStorage.setItem('lang', 'pt');
            },
          },
        ]}
      ></IonActionSheet>
    </>
  );
};

export default Menu;
